import { Check } from "lucide-react";
import { SERVICES } from "@/data/content";
import { SectionHeading, CTAButton } from "./ui";
import { Reveal } from "./Reveal";

export default function ServicesSection() {
  return (
    <section id="servizi" className="relative bg-brand-ivory py-20 lg:py-32 grain-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <SectionHeading eyebrow={SERVICES.eyebrow} title={SERVICES.title} />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.packages.map((p, i) => (
            <Reveal key={p.name} delay={i % 2}>
              <div
                data-testid={`service-card-${p.name.toLowerCase().replace(/\s|&/g, "-")}`}
                className={`flex h-full flex-col rounded-sm p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 ${
                  p.featured
                    ? "bg-brand-bordeaux text-brand-ivory shadow-[0_20px_60px_-20px_rgba(90,16,37,0.5)]"
                    : "bg-brand-cream border border-brand-bordeaux/12 hover:border-brand-bordeaux/30"
                }`}
              >
                {p.featured && (
                  <span className="self-start mb-4 rounded-full bg-brand-gold px-3 py-1 text-[10px] uppercase tracking-editorial font-semibold text-brand-graphite">
                    Punto di partenza
                  </span>
                )}
                <h3 className={`font-serif text-3xl ${p.featured ? "text-brand-ivory" : "text-brand-bordeaux"}`}>
                  {p.name}
                </h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className={`font-serif text-4xl ${p.featured ? "text-brand-gold" : "text-brand-graphite"}`}>
                    {p.price}
                  </span>
                  <span className={`text-sm ${p.featured ? "text-brand-ivory/70" : "text-brand-graphite/60"}`}>
                    {p.priceNote}
                  </span>
                </div>
                {p.duration && (
                  <p className={`mt-2 font-mono text-xs ${p.featured ? "text-brand-ivory/60" : "text-brand-graphite/55"}`}>
                    Durata: {p.duration}
                  </p>
                )}
                <p className={`mt-4 text-sm leading-relaxed ${p.featured ? "text-brand-ivory/80" : "text-brand-graphite/70"}`}>
                  {p.description}
                </p>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-brand-gold" : "text-brand-green"}`} />
                      <span className={p.featured ? "text-brand-ivory/85" : "text-brand-graphite/80"}>{f}</span>
                    </li>
                  ))}
                </ul>

                {p.output && (
                  <p className={`mt-6 border-t pt-4 text-xs leading-relaxed ${p.featured ? "border-brand-ivory/15 text-brand-ivory/70" : "border-brand-bordeaux/10 text-brand-graphite/60"}`}>
                    {p.output}
                  </p>
                )}

                <div className="mt-8">
                  <CTAButton
                    to={p.route}
                    variant={p.featured ? "gold" : "outline"}
                    className="w-full"
                    data-testid={`service-cta-${i}`}
                  >
                    {p.cta}
                  </CTAButton>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Enterprise */}
        <Reveal delay={1}>
          <div
            data-testid="service-enterprise"
            className="mt-8 rounded-sm bg-brand-graphite p-8 lg:p-12 grain-overlay relative flex flex-col lg:flex-row lg:items-center justify-between gap-8"
          >
            <div className="max-w-2xl">
              <h3 className="font-serif text-3xl text-brand-ivory">{SERVICES.enterprise.name}</h3>
              <p className="mt-2 font-mono text-sm text-brand-gold">{SERVICES.enterprise.price}</p>
              <p className="mt-4 text-sm leading-relaxed text-brand-ivory/70">{SERVICES.enterprise.description}</p>
            </div>
            <CTAButton href="#lead" variant="gold" data-testid="enterprise-cta" className="shrink-0">
              {SERVICES.enterprise.cta}
            </CTAButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
